import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ShieldAlert, Trash2, Edit3, Crown, Sparkles, Clock } from "lucide-react";

export default function MyAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [boost, setBoost] = useState({ active: false, active_until: null });
  const [boostBusy, setBoostBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/boost/me").then((r) => setBoost(r.data)).catch(() => {});
  }, []);

  const buyBoost = async () => {
    setBoostBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "boost_24h", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBoostBusy(false); }
  };

  const deleteAcct = async () => {
    if (!(await confirmDialog({ title: "Slet din konto?", body: "Dette vil permanent slette din profil og konto. Handlingen kan ikke fortrydes.", tone: "danger", confirmText: "Ja, slet konto" }))) return;
    try {
      await api.delete("/profile");
      await logout();
      navigate("/");
    } catch (e) { setErr(formatApiError(e)); }
  };

  const sub = user?.subscription || {};

  return (
    <div className="max-w-3xl mx-auto px-4 py-7" data-testid="my-account-page">
      <div className="eyebrow">Konto</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">Min konto</h1>

      <div className="mt-8 card-soft p-6">
        <div className="text-sm eyebrow">Identitet</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-brown-600">
          <div><span className="text-brown-500 text-sm">Navn</span><div>{user?.name}</div></div>
          <div><span className="text-brown-500 text-sm">E-mail</span><div>{user?.email}</div></div>
          <div><span className="text-brown-500 text-sm">Rolle</span><div className="capitalize">{user?.role}</div></div>
          <div><span className="text-brown-500 text-sm">Profil-status</span><div className="capitalize">{profile?.status || "ikke påbegyndt"}</div></div>
        </div>
      </div>

      <div className="mt-6 card-soft p-6">
        <div className="flex items-center gap-2 eyebrow"><Crown className="h-4 w-4 text-gold-500" /> Medlemskab</div>
        {sub.active ? (
          <div className="mt-3 text-brown-600">
            <div>Niveau: <strong className="capitalize">{sub.tier?.replace("_", " ")}</strong></div>
            <div>Gyldig indtil: <strong>{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString("da-DK") : "—"}</strong></div>
          </div>
        ) : (
          <div className="mt-3 text-brown-600 text-sm">
            Du har ikke et aktivt medlemskab. <Link to="/pricing" className="text-olive-600 hover:underline">Se planer →</Link>
          </div>
        )}
      </div>

      <div className="mt-6 card-soft p-6 flex items-center justify-between gap-4">
        <div>
          <div className="eyebrow">Profil</div>
          <p className="mt-2 text-brown-600 text-sm">Redigér din profil, opdatér fotos, eller indsend til ny gennemgang.</p>
        </div>
        <Link to="/profile" className="btn-secondary text-sm py-2 px-4" data-testid="edit-profile-link"><Edit3 className="h-4 w-4" /> Redigér</Link>
      </div>

      <div className="mt-6 card-soft p-6 flex items-center justify-between gap-4 flex-wrap bg-gradient-to-br from-gold-100/60 to-sand-50 border-gold-500/30">
        <div>
          <div className="eyebrow flex items-center gap-2 text-gold-700"><Sparkles className="h-3 w-3" /> Profil-boost</div>
          {boost.active ? (
            <p className="mt-2 text-sm text-brown-700 flex items-center gap-1">
              <Clock className="h-4 w-4 text-gold-600" />
              Aktiv indtil <strong className="ml-1">{new Date(boost.active_until).toLocaleString("da-DK")}</strong> — du vises øverst i forslag.
            </p>
          ) : (
            <p className="mt-2 text-brown-600 text-sm">Bliv fremhævet øverst i forslag i 24 timer. 5× flere profilvisninger i gennemsnit. Ingen abonnement kræves.</p>
          )}
        </div>
        {!boost.active && (
          <button onClick={buyBoost} disabled={boostBusy} className="btn-primary text-sm py-2 px-4 disabled:opacity-60" data-testid="buy-boost">
            <Sparkles className="h-4 w-4" /> {boostBusy ? "Åbner…" : "Boost 24t"}
          </button>
        )}
      </div>

      <div className="mt-6 card-soft p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Verificering</div>
          <p className="mt-2 text-brown-600 text-sm">Bliv verificeret på selfie eller ID-niveau — verificerede profiler får mere tillid og synlighed.</p>
        </div>
        <Link to="/verify" className="btn-secondary text-sm py-2 px-4" data-testid="verify-link">Verificér</Link>
      </div>

      <div className="mt-6 card-soft p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Familie-cirkel</div>
          <p className="mt-2 text-brown-600 text-sm">Invitér en wali eller pårørende til en privat, read-only oversigt over din rejse.</p>
        </div>
        <Link to="/family" className="btn-secondary text-sm py-2 px-4" data-testid="family-link">Åbn familie-cirkel</Link>
      </div>

      <div className="mt-6 card-soft p-6 border-destructive/30 bg-destructive/5">
        <div className="eyebrow flex items-center gap-2 text-destructive"><ShieldAlert className="h-4 w-4" /> Farezone</div>
        <p className="mt-2 text-sm text-brown-600">Slet din profil og konto permanent. Dette kan ikke fortrydes.</p>
        {err && <div className="mt-3 text-sm text-destructive">{err}</div>}
        <button onClick={deleteAcct} className="mt-4 inline-flex items-center gap-2 rounded-full border border-destructive/40 px-5 py-2 text-destructive hover:bg-destructive/10 text-sm" data-testid="delete-account-btn">
          <Trash2 className="h-4 w-4" /> Slet min konto
        </button>
      </div>
    </div>
  );
}
